/**
 * TradeHistory — Recent trade table for a token.
 *
 * Owns its own data fetching via useTradeHistory hook.
 * Displays buy/sell trades with type indicators, amounts, and explorer links.
 *
 * Story 4.4 — Task 2
 */

import type { TradeHistoryEntry } from "@repo/shared-types";
import { useCallback } from "react";

import { useTradeHistory } from "../../hooks/api/use-trade-history";
import { copyToClipboard } from "../../lib/clipboard";
import { cn } from "../../lib/cn";
import { formatAddress } from "../../lib/format";
import {
	formatEth,
	formatPrice,
	formatRelativeTime,
	formatTokenAmount,
} from "../../lib/format-number";
import { getBlockExplorerUrl } from "../../lib/wagmi-config";
import { useToast } from "../ui/Toast";

type TradeHistoryProps = {
	tokenId: string;
};

function TradeTypeCell({ type }: { type: "buy" | "sell" }) {
	const isBuy = type === "buy";
	return (
		<span
			className={cn(
				"flex items-center gap-1 text-xs font-medium",
				isBuy ? "text-green-600" : "text-red-500"
			)}
		>
			<span aria-hidden="true">{isBuy ? "\u2191" : "\u2193"}</span>
			{isBuy ? "Buy" : "Sell"}
		</span>
	);
}

function WalletCell({ address, onCopy }: { address: string; onCopy: () => void }) {
	return (
		<button
			type="button"
			onClick={onCopy}
			className="font-mono text-xs text-pastel-black hover:text-pastel-vibrant-blue transition-colors"
			title={`Copy ${address}`}
		>
			{formatAddress(address)}
		</button>
	);
}

function TradeRow({
	trade,
	onCopyAddress,
}: { trade: TradeHistoryEntry; onCopyAddress: (addr: string) => void }) {
	const explorerUrl = `${getBlockExplorerUrl()}/tx/${trade.txHash}`;

	return (
		<tr className="border-b border-pastel-border last:border-b-0 hover:bg-pastel-light-gray/50 transition-colors">
			<td className="py-3 px-3">
				<TradeTypeCell type={trade.type} />
			</td>
			<td className="py-3 px-3 text-xs text-pastel-black">
				{formatTokenAmount(trade.tokenAmount)}
			</td>
			<td className="py-3 px-3 text-xs text-pastel-black">{formatPrice(trade.priceAtTrade)}</td>
			<td className="py-3 px-3 text-xs text-pastel-black">{formatEth(trade.ethAmount)}</td>
			<td className="py-3 px-3">
				<WalletCell
					address={trade.walletAddress}
					onCopy={() => onCopyAddress(trade.walletAddress)}
				/>
			</td>
			<td className="py-3 px-3">
				<div className="flex items-center gap-1">
					<span
						className="text-xs text-pastel-gray"
						title={new Date(trade.timestamp).toLocaleString()}
					>
						{formatRelativeTime(trade.timestamp)}
					</span>
					<a
						href={explorerUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-pastel-light-gray transition-colors"
						title="View transaction"
					>
						<span className="sr-only">View transaction on explorer</span>
						<svg
							className="h-3 w-3 text-pastel-gray"
							viewBox="0 0 20 20"
							fill="currentColor"
							aria-hidden="true"
						>
							<path
								fillRule="evenodd"
								d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z"
								clipRule="evenodd"
							/>
							<path
								fillRule="evenodd"
								d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z"
								clipRule="evenodd"
							/>
						</svg>
					</a>
				</div>
			</td>
		</tr>
	);
}

export function TradeHistory({ tokenId }: TradeHistoryProps) {
	const { data, isLoading, error, refetch } = useTradeHistory(tokenId);
	const { addToast } = useToast();

	const trades = data?.data ?? [];
	const dataSource = data?.meta?.dataSource ?? "indexer";

	const handleCopyAddress = useCallback(
		async (address: string) => {
			try {
				await copyToClipboard(address);
				addToast({ type: "success", title: "Address copied!" });
			} catch {
				addToast({ type: "error", title: "Failed to copy" });
			}
		},
		[addToast]
	);

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<p className="text-sm text-pastel-gray mb-3">Unable to load trade history</p>
				<button
					type="button"
					onClick={() => void refetch()}
					className="px-4 py-2 text-sm font-medium text-white bg-pastel-vibrant-blue rounded-lg hover:opacity-90 transition-opacity"
				>
					Retry
				</button>
			</div>
		);
	}

	if (isLoading) {
		return <TradeHistorySkeleton />;
	}

	if (trades.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<svg
					className="h-10 w-10 text-pastel-light-gray mb-3"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.5"
					aria-hidden="true"
				>
					<path
						d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				<p className="text-sm text-pastel-gray">No trades yet — be the first to trade!</p>
			</div>
		);
	}

	return (
		<div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[600px]">
					<thead>
						<tr className="border-b border-pastel-border">
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Type
							</th>
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Amount
							</th>
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Price
							</th>
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								ETH
							</th>
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Wallet
							</th>
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Time
							</th>
						</tr>
					</thead>
					<tbody>
						{trades.map((trade) => (
							<TradeRow key={trade.txHash} trade={trade} onCopyAddress={handleCopyAddress} />
						))}
					</tbody>
				</table>
			</div>
			{dataSource === "seeded" && (
				<div className="mt-3">
					<span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
						Sample Data
					</span>
				</div>
			)}
		</div>
	);
}

export function TradeHistorySkeleton() {
	return (
		<div className="animate-pulse">
			<div className="border-b border-pastel-border py-2 px-3 flex gap-3">
				{Array.from({ length: 6 }).map((_, i) => (
					<div
						key={`th-skeleton-${String(i)}`}
						className="h-3 bg-pastel-light-gray rounded"
						style={{ width: `${60 + i * 10}px` }}
					/>
				))}
			</div>
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={`tr-skeleton-${String(i)}`}
					className="border-b border-pastel-border py-3 px-3 flex gap-3"
				>
					{Array.from({ length: 6 }).map((_, j) => (
						<div
							key={`td-skeleton-${String(i)}-${String(j)}`}
							className="h-4 bg-pastel-light-gray rounded"
							style={{ width: `${50 + j * 8}px` }}
						/>
					))}
				</div>
			))}
		</div>
	);
}
